import { withFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Grid, Modal as BootstrapModal, Row, Tab, Tabs } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { useTranslation, withTranslation } from 'react-i18next';
import { assignVisitBranches, editVisit, getVisit } from '../../../api/protocol';
import { HandleApiError } from '../../../components/Notifications/APIErrorHandler';
import { Save } from '../../../components/Icons/Icons';
import { Success } from '../../../components/Notifications/Notifications';
import { OffsetTypes, Types, VisitStages } from '../../../components/Protocols/Visit/Enums';
import { VisitHeaderForm } from '../../../components/Protocols/Visit/Header';
import { getProtocolVisitHeaderSchema } from "../../../components/Validations/FormValidationSchema";
import { useProtocolVisits } from '../Hooks';
import { VisitBranchSelector } from '../Branches';

export const EditVisitModal = (props) => {
    const { isShowing, hide, protocolId, visitId, submit, onBranchSaved } = props;
    const { t } = useTranslation();

    return isShowing ? ReactDOM.createPortal(
        <React.Fragment>
            <BootstrapModal show={true} onHide={hide} bsSize="large" backdrop="static" >
                <BootstrapModal.Header closeButton>
                    <BootstrapModal.Title>{t('protocols.practitioner.editPractitionerTitle')}</BootstrapModal.Title>
                </BootstrapModal.Header>
                <BootstrapModal.Body>
                    <EditVisitContainer protocolId={protocolId} visitId={visitId} onClose={hide} onSubmit={submit} onBranchSaved={onBranchSaved} />
                </BootstrapModal.Body>
            </BootstrapModal>
        </React.Fragment>, document.body
    ) : null;
};

const EditVisitContainer = (props) => {
    const { protocolId, visitId } = props;
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [visit, setVisit] = useState(null);
    const visits = useProtocolVisits(protocolId);
    useEffect(() => {
        const fetchData = () => {
            setIsLoading(true);
            getVisit(protocolId, visitId)
                .then(response => {
                    var resVisit = response.data;
                    console.log(resVisit);
                    resVisit.type = Types().find(x => x.id == resVisit.type);
                    resVisit.stage = VisitStages().find(x => x.id == resVisit.stage);
                    resVisit.offsetType = OffsetTypes().find(x => x.id == resVisit.offsetType);
                    resVisit.branchIds = (resVisit.branches || []).map(b => b.branchId);

                    setVisit(resVisit);
                    setIsLoading(false);
                }).catch(error => {
                    HandleApiError(error);
                    setIsLoading(false);
                });
        }

        fetchData();
    }, [protocolId, visitId]);

    const handleSubmit = () => {
        props.onSubmit();
    }
    const handleCancel = () => {
        props.onClose();
    }

    if (isLoading || !visit)
        return null;

    return (
        <Tabs defaultActiveKey={1} id="edit-visit-tabs">
            <Tab eventKey={1} title={t("protocols.visit.visitData")}>
                <div style={{ paddingTop: '15px' }}>
                    <EditVisitFormik
                        protocolId={protocolId}
                        visit={visit}
                        visits={visits}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                    />
                </div>
            </Tab>
            <Tab eventKey={2} title={t("protocols.branches.title")}>
                <div style={{ paddingTop: '15px' }}>
                    <VisitBranchTabContainer
                        protocolId={protocolId}
                        visit={visit}
                        onClose={handleCancel}
                        onSave={props.onBranchSaved}
                    />
                </div>
            </Tab>
        </Tabs>
    );
}
EditVisitContainer.propTypes = {
    protocolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    visitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const VisitBranchTabContainer = ({ protocolId, visit, onClose, onSave }) => {
    const { t } = useTranslation();
    const [branchIds, setBranchIds] = useState(visit.branchIds || []);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = () => {
        setIsSaving(true);
        assignVisitBranches(protocolId, visit.crossVersionId, branchIds)
            .then(() => {
                setIsSaving(false);
                Success("protocolNotifications.visit_Updated");
                if (onSave) onSave();
                onClose();
            })
            .catch(error => {
                setIsSaving(false);
                HandleApiError(error);
            });
    };

    return (
        <Grid fluid>
            <Row>
                <Col sm={12}>
                    <VisitBranchSelector
                        protocolId={protocolId}
                        selectedBranchIds={branchIds}
                        onChange={setBranchIds}
                    />
                </Col>
            </Row>
            <Row style={{ marginTop: '10px' }}>
                <Col sm={12}>
                    <button type="button" className="btn btn-success btn-right" onClick={handleSave} disabled={isSaving}>
                        <Save /> {t("buttons.save")}
                    </button>
                    <button type="button" className="btn btn-default btn-right" onClick={onClose} style={{ marginRight: '10px' }} disabled={isSaving}>
                        {t("buttons.cancel")}
                    </button>
                </Col>
            </Row>
        </Grid>
    );
};

const EditVisitFormik = withTranslation()(withFormik({
    mapPropsToValues: (props) => ({
        visit: {
            ...props.visit,
            offsetFrom: props.visits?.find(x => x.crossVersionId === props.visit?.offsetFrom) ?? null
        }
    }),
    enableReinitialize: true,

    validationSchema: getProtocolVisitHeaderSchema(),

    handleSubmit: (value, formikBag) => {
        let visit = value.visit;
        visit.type = visit.type.id;
        visit.stage = visit.stage.id;
        visit.offsetType = visit.offsetType?.id
        visit.offsetFrom = visit.offsetFrom?.crossVersionId

        editVisit(formikBag.props.protocolId, value.visit.crossVersionId, visit)
            .then(res => {
                formikBag.setSubmitting(false);
                Success("protocolNotifications.visit_Updated");
                formikBag.props.onSubmit(res.data);
            }).catch(error => {
                console.log(error);
                formikBag.setSubmitting(false);
                HandleApiError(error);
            });
    },

    displayName: 'EditVisit',

})(VisitHeaderForm));

EditVisitFormik.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    protocolId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    visit: PropTypes.object.isRequired,
};